/**
 * Copyright (C) 2025 GIP-RECIA, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { TemplateResult } from 'lit'
import { localized, msg, str } from '@lit/localize'
import { componentName } from 'common/config.ts'
import { css, html, LitElement, unsafeCSS } from 'lit'
import { property } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { name } from '../package.json'
import langHelper from './helpers/langHelper.ts'
import styles from './style.scss?inline'
import { IdpId } from './types/IdpIdType.ts'
import { setLocale } from './utils/localizationUtils.ts'

@localized()
export class ReciaWayf extends LitElement {
  @property({ type: String, attribute: 'cas-url' })
  casUrl?: string

  @property({ type: Array, attribute: 'idp-ids' })
  idpIds?: IdpId[]

  @property({ type: String, attribute: 'svg-url' })
  svgUrl?: string = `./${name}.spritemap.svg`

  constructor() {
    super()
    const lang = langHelper.getPageLang()
    setLocale(lang)
    langHelper.setLocale(lang)
  }

  static i18n(): Record<IdpId, string> {
    return {
      [IdpId.ParentEleveEN]: msg(str`Élève ou parent de l'éducation nationale\n(ÉduConnect)`),
      [IdpId.Catel]: msg(str`Personnel de l'éducation nationale`),
      [IdpId.Agri]: msg(str`Élève, parent ou personnel de l'enseignement agricole (ÉduAgri)`),
      [IdpId.RCVL]: msg(str`Personnel de la Région Centre-Val de Loire`),
      [IdpId.CD28]: msg(str`Personnel du département d'Eure-et-Loir`),
      [IdpId.AutresPublics]: msg(str`Autre public\n(utilisateur local, entreprise,...)`),
    }
  }

  render(): TemplateResult {
    return html`
      <ul>
        ${
          repeat(
            (this.idpIds ?? []).filter(idpId => Object.values(IdpId).includes(idpId)),
            idpId => idpId,
            idpId => html`
              <li>
                <a id=${idpId} href="${this.casUrl}&idpId=${idpId}">
                  <svg class="wayf-profile" aria-hidden="true" >
                    <use href="${this.svgUrl}#${idpId}"></use>
                  </svg>
                  <span>${ReciaWayf.i18n()[idpId]}</span>
                </a>
              </li>
            `,
          )
        }
      </ul>
    `
  }

  static styles = css`${unsafeCSS(styles)}`
}

const tagName = componentName(name)

if (!customElements.get(tagName)) {
  customElements.define(tagName, ReciaWayf)
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: ReciaWayf
  }
}
